import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, BookOpenIcon, HeartIcon, SunIcon, UsersIcon, CheckIcon, StarIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
interface BookPageProps {
  onBack: () => void;
}
const features = [{
  icon: BookOpenIcon,
  title: 'Daily Devotionals',
  description: 'Structured daily readings with scripture references and guided reflections.'
}, {
  icon: HeartIcon,
  title: 'Prayer Prompts',
  description: 'Focused prayer points to deepen your conversation with God each day.'
}, {
  icon: SunIcon,
  title: 'Morning & Evening',
  description: 'Start and end your day anchored in the Word with morning and evening prayers.'
}, {
  icon: UsersIcon,
  title: 'Group Friendly',
  description: 'Perfect for individual use or small group prayer challenges.'
}];
const chapters = ['Week 1: Establishing a Foundation of Prayer', 'Week 2: Praying with Authority', 'Week 3: Intercession & Spiritual Warfare', 'Week 4: Praying for Purpose & Direction', 'Week 5: Walking in Answered Prayer', 'Bonus: 7 Declarations for Daily Victory'];
export function BookPage({
  onBack
}: BookPageProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    format: 'digital'
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div className="min-h-screen bg-cream" data-id="element-302">
      {/* Top Bar */}
      <div className="bg-dark sticky top-0 z-50" data-id="element-303">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between" data-id="element-304">
          <button onClick={onBack} className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors font-inter text-sm" data-id="element-305">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-306" />
            Back to Home
          </button>
          <a href="#" className="text-gold font-playfair font-bold text-lg tracking-wider uppercase" data-id="element-307">
            APM Store
          </a>
        </div>
      </div>

      {/* Hero / Product Section */}
      <section className="bg-charcoal py-20 md:py-32 relative overflow-hidden" data-id="element-308">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(201,168,76,0.08)_0%,transparent_60%)]" data-id="element-309" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-310">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-311">
            {/* Book Cover */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} className="flex justify-center" data-id="element-312">
              <div className="relative" data-id="element-313">
                <div className="absolute -inset-6 bg-gold/10 blur-3xl rounded-full" data-id="element-314" />
                <div className="relative w-72 md:w-80 aspect-[2/3] bg-dark border border-gold/30 shadow-2xl rounded-sm overflow-hidden" data-id="element-315">
                  <img src="https://images.unsplash.com/photo-1544947950-fa07a98d237f?q=80&w=600&auto=format&fit=crop" alt="40 Days Prayer Guide" className="w-full h-full object-cover" data-id="element-316" />
                  <div className="absolute inset-0 border-l-[6px] border-white/10" data-id="element-317" />
                </div>
                {/* Price badge */}
                <motion.div initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.5,
                delay: 0.6
              }} className="absolute -bottom-4 -right-4 bg-gold text-dark w-20 h-20 rounded-full flex flex-col items-center justify-center shadow-xl" data-id="element-318">
                  <span className="text-xs font-semibold" data-id="element-319">Only</span>
                  <span className="text-xl font-bold font-playfair" data-id="element-320">
                    $14.99
                  </span>
                </motion.div>
              </div>
            </motion.div>

            {/* Book Details */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-id="element-321">
              <div className="flex items-center gap-3 mb-6" data-id="element-322">
                <div className="h-[1px] w-10 bg-gold" data-id="element-323" />
                <span className="text-gold font-semibold tracking-[0.2em] text-xs uppercase" data-id="element-324">
                  By Apostle Akintunde Phillips
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white leading-tight mb-6" data-id="element-325">
                40 Days <br data-id="element-326" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-goldLight to-gold" data-id="element-327">
                  Prayer Guide
                </span>
              </h1>

              <p className="text-white/60 font-inter text-lg leading-relaxed mb-8 max-w-lg" data-id="element-328">
                A powerful 40-day journey of prayer and devotion designed to
                deepen your relationship with God and transform your prayer
                life. This guide will take you from where you are to where God
                wants you to be.
              </p>

              <div className="flex items-center gap-2 mb-8" data-id="element-329">
                <div className="flex gap-1" data-id="element-330">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-gold fill-gold" data-id="element-331" />)}
                </div>
                <span className="text-white/50 font-inter text-sm" data-id="element-332">
                  Transforming prayer lives
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start gap-4 mb-10" data-id="element-333">
                <a href="#order-form" className="px-10 py-4 bg-gold hover:bg-goldLight text-dark font-bold tracking-wide transition-all duration-300 rounded-sm text-lg" data-id="element-334">
                  Buy Now — $14.99
                </a>
                <a href="#preview" className="px-8 py-4 border border-white/20 text-white hover:border-gold hover:text-gold font-semibold tracking-wide transition-all duration-300 rounded-sm" data-id="element-335">
                  Preview Contents
                </a>
              </div>

              <div className="flex items-center gap-6 text-white/40 font-inter text-sm" data-id="element-336">
                <span className="flex items-center gap-2" data-id="element-337">
                  <CheckIcon className="w-4 h-4 text-gold" data-id="element-338" /> Paperback &
                  Digital
                </span>
                <span className="flex items-center gap-2" data-id="element-339">
                  <CheckIcon className="w-4 h-4 text-gold" data-id="element-340" /> Instant Download
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features / What's Inside */}
      <section className="py-20 md:py-28 bg-cream" data-id="element-341">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-342">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-16" data-id="element-343">
            <span className="text-gold font-semibold tracking-[0.2em] text-sm uppercase mb-4 block" data-id="element-344">
              What's Inside
            </span>
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-charcoal" data-id="element-345">
              Transform Your Prayer Life
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-346">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="text-center" data-id="element-347">
                  <div className="w-16 h-16 bg-charcoal rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-348">
                    <Icon className="w-7 h-7 text-gold" data-id="element-349" />
                  </div>
                  <h3 className="text-xl font-playfair font-bold text-charcoal mb-3" data-id="element-350">
                    {feature.title}
                  </h3>
                  <p className="text-charcoal/60 font-inter leading-relaxed" data-id="element-351">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section id="preview" className="py-20 md:py-28 bg-dark" data-id="element-352">
        <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-353">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-16" data-id="element-354">
            <span className="text-gold font-semibold tracking-[0.2em] text-sm uppercase mb-4 block" data-id="element-355">
              Preview
            </span>
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-white" data-id="element-356">
              Inside the Guide
            </h2>
          </motion.div>

          <div className="space-y-0 border-t border-white/10" data-id="element-357">
            {chapters.map((chapter, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.08
          }} className="flex items-center gap-6 py-5 border-b border-white/10" data-id="element-358">
                <span className="text-gold font-playfair font-bold text-lg w-8 shrink-0" data-id="element-359">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-white/80 font-inter text-lg" data-id="element-360">
                  {chapter}
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 md:py-28 bg-charcoal" data-id="element-361">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center" data-id="element-362">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-363">
            <span className="text-gold text-6xl font-playfair leading-none block mb-6" data-id="element-364">
              "
            </span>
            <p className="text-2xl md:text-3xl font-playfair text-white leading-relaxed mb-8" data-id="element-365">
              This prayer guide completely transformed my morning routine. By
              day 15, I was experiencing a depth of prayer I never thought
              possible. Every believer needs this book.
            </p>
            <div data-id="element-366">
              <p className="text-gold font-bold font-inter" data-id="element-367">
                Minister Grace T.
              </p>
              <p className="text-white/40 font-inter text-sm mt-1" data-id="element-368">
                Prayer Group Leader
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Order Form */}
      <section id="order-form" className="py-20 md:py-28 bg-cream" data-id="element-369">
        <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-370">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-12" data-id="element-371">
            <span className="text-gold font-semibold tracking-[0.2em] text-sm uppercase mb-4 block" data-id="element-372">
              Order Now
            </span>
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-charcoal mb-4" data-id="element-373">
              Get Your Copy
            </h2>
            <p className="text-charcoal/60 font-inter text-lg max-w-lg mx-auto" data-id="element-374">
              Fill out the form below to place your order for the 40 Days Prayer
              Guide.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} data-id="element-375">
            {submitted ? <div className="bg-white p-12 shadow-xl border border-charcoal/5 rounded-sm text-center" data-id="element-376">
                <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-377">
                  <CheckIcon className="w-10 h-10 text-gold" data-id="element-378" />
                </div>
                <h3 className="text-3xl font-playfair font-bold text-charcoal mb-4" data-id="element-379">
                  Order Received!
                </h3>
                <p className="text-charcoal/60 font-inter text-lg mb-2" data-id="element-380">
                  Thank you for your order, {formData.fullName}!
                </p>
                <p className="text-charcoal/50 font-inter mb-8" data-id="element-381">
                  A confirmation email will be sent to{' '}
                  <span className="text-gold font-medium" data-id="element-382">
                    {formData.email}
                  </span>{' '}
                  with your order details
                  {formData.format === 'digital' ? ' and download link.' : ' and shipping information.'}
                </p>
                <button onClick={() => setSubmitted(false)} className="px-8 py-3 border border-charcoal/20 text-charcoal hover:border-gold hover:text-gold font-semibold tracking-wide transition-all duration-300 rounded-sm" data-id="element-383">
                  Place Another Order
                </button>
              </div> : <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 shadow-xl border border-charcoal/5 rounded-sm" data-id="element-384">
                {/* Format Selection */}
                <div className="mb-10" data-id="element-385">
                  <label className="block text-sm font-semibold text-charcoal mb-4" data-id="element-386">
                    Choose Format
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-387">
                    <label className={`flex items-center gap-4 p-4 border-2 rounded-sm cursor-pointer transition-all ${formData.format === 'digital' ? 'border-gold bg-gold/5' : 'border-charcoal/10 hover:border-charcoal/30'}`} data-id="element-388">
                      <input type="radio" name="format" value="digital" checked={formData.format === 'digital'} onChange={handleChange} className="sr-only" data-id="element-389" />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.format === 'digital' ? 'border-gold' : 'border-charcoal/30'}`} data-id="element-390">
                        {formData.format === 'digital' && <div className="w-2.5 h-2.5 rounded-full bg-gold" data-id="element-391" />}
                      </div>
                      <div data-id="element-392">
                        <span className="font-semibold text-charcoal block" data-id="element-393">
                          Digital (PDF)
                        </span>
                        <span className="text-charcoal/50 text-sm" data-id="element-394">
                          Instant download — $14.99
                        </span>
                      </div>
                    </label>
                    <label className={`flex items-center gap-4 p-4 border-2 rounded-sm cursor-pointer transition-all ${formData.format === 'paperback' ? 'border-gold bg-gold/5' : 'border-charcoal/10 hover:border-charcoal/30'}`} data-id="element-395">
                      <input type="radio" name="format" value="paperback" checked={formData.format === 'paperback'} onChange={handleChange} className="sr-only" data-id="element-396" />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.format === 'paperback' ? 'border-gold' : 'border-charcoal/30'}`} data-id="element-397">
                        {formData.format === 'paperback' && <div className="w-2.5 h-2.5 rounded-full bg-gold" data-id="element-398" />}
                      </div>
                      <div data-id="element-399">
                        <span className="font-semibold text-charcoal block" data-id="element-400">
                          Paperback
                        </span>
                        <span className="text-charcoal/50 text-sm" data-id="element-401">
                          Shipped to you — $14.99
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-402">
                  <div data-id="element-403">
                    <label htmlFor="fullName" className="block text-sm font-semibold text-charcoal mb-2" data-id="element-404">
                      Full Name *
                    </label>
                    <input type="text" id="fullName" name="fullName" required value={formData.fullName} onChange={handleChange} className="w-full px-4 py-3 bg-cream border border-charcoal/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors rounded-sm" placeholder="John Doe" data-id="element-405" />
                  </div>
                  <div data-id="element-406">
                    <label htmlFor="email" className="block text-sm font-semibold text-charcoal mb-2" data-id="element-407">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-cream border border-charcoal/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors rounded-sm" placeholder="john@example.com" data-id="element-408" />
                  </div>
                </div>

                <div className="mb-6" data-id="element-409">
                  <label htmlFor="phone" className="block text-sm font-semibold text-charcoal mb-2" data-id="element-410">
                    Phone Number
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-cream border border-charcoal/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors rounded-sm" placeholder="(240) 555-0123" data-id="element-411" />
                </div>

                {/* Shipping Address (only for paperback) */}
                {formData.format === 'paperback' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="mb-6 space-y-6" data-id="element-412">
                    <div className="pt-6 border-t border-charcoal/10" data-id="element-413">
                      <h4 className="text-sm font-semibold text-charcoal mb-4 flex items-center gap-2" data-id="element-414">
                        <TruckIcon className="w-4 h-4 text-gold" data-id="element-415" />
                        Shipping Address
                      </h4>
                      <div className="space-y-6" data-id="element-416">
                        <div data-id="element-417">
                          <label htmlFor="address" className="block text-sm font-semibold text-charcoal mb-2" data-id="element-418">
                            Street Address *
                          </label>
                          <input type="text" id="address" name="address" required value={formData.address} onChange={handleChange} className="w-full px-4 py-3 bg-cream border border-charcoal/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors rounded-sm" placeholder="123 Main Street" data-id="element-419" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-420">
                          <div data-id="element-421">
                            <label htmlFor="city" className="block text-sm font-semibold text-charcoal mb-2" data-id="element-422">
                              City *
                            </label>
                            <input type="text" id="city" name="city" required value={formData.city} onChange={handleChange} className="w-full px-4 py-3 bg-cream border border-charcoal/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors rounded-sm" placeholder="Bladensburg" data-id="element-423" />
                          </div>
                          <div data-id="element-424">
                            <label htmlFor="state" className="block text-sm font-semibold text-charcoal mb-2" data-id="element-425">
                              State *
                            </label>
                            <input type="text" id="state" name="state" required value={formData.state} onChange={handleChange} className="w-full px-4 py-3 bg-cream border border-charcoal/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors rounded-sm" placeholder="MD" data-id="element-426" />
                          </div>
                          <div className="col-span-2 md:col-span-1" data-id="element-427">
                            <label htmlFor="zip" className="block text-sm font-semibold text-charcoal mb-2" data-id="element-428">
                              ZIP Code *
                            </label>
                            <input type="text" id="zip" name="zip" required value={formData.zip} onChange={handleChange} className="w-full px-4 py-3 bg-cream border border-charcoal/10 focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-colors rounded-sm" placeholder="20710" data-id="element-429" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {/* Order Summary */}
                <div className="bg-cream p-6 rounded-sm mb-8 border border-charcoal/5" data-id="element-430">
                  <div className="flex items-center justify-between mb-2" data-id="element-431">
                    <span className="text-charcoal/70 font-inter" data-id="element-432">
                      40 Days Prayer Guide (
                      {formData.format === 'digital' ? 'Digital' : 'Paperback'})
                    </span>
                    <span className="text-charcoal font-semibold" data-id="element-433">$14.99</span>
                  </div>
                  {formData.format === 'paperback' && <div className="flex items-center justify-between mb-2" data-id="element-434">
                      <span className="text-charcoal/70 font-inter" data-id="element-435">
                        Shipping
                      </span>
                      <span className="text-charcoal font-semibold" data-id="element-436">Free</span>
                    </div>}
                  <div className="border-t border-charcoal/10 mt-3 pt-3 flex items-center justify-between" data-id="element-437">
                    <span className="text-charcoal font-bold font-inter" data-id="element-438">
                      Total
                    </span>
                    <span className="text-gold font-bold font-playfair text-2xl" data-id="element-439">
                      $14.99
                    </span>
                  </div>
                </div>

                <button type="submit" className="w-full py-4 bg-gold hover:bg-goldLight text-dark font-bold tracking-wide transition-colors rounded-sm text-lg" data-id="element-440">
                  Place Order — $14.99
                </button>

                <div className="flex items-center justify-center gap-4 mt-6 text-charcoal/40 font-inter text-xs" data-id="element-441">
                  <span className="flex items-center gap-1" data-id="element-442">
                    <ShieldCheckIcon className="w-4 h-4" data-id="element-443" /> Secure Checkout
                  </span>
                  <span data-id="element-444">•</span>
                  <span data-id="element-445">100% Satisfaction Guaranteed</span>
                </div>
              </form>}
          </motion.div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-dark py-8 border-t border-white/10" data-id="element-446">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-447">
          <p className="text-gray-500 font-inter text-sm" data-id="element-448">
            © {new Date().getFullYear()} Apostle Phillips Ministries
          </p>
          <button onClick={onBack} className="text-gold hover:text-goldLight font-inter text-sm transition-colors" data-id="element-449">
            ← Return to Main Site
          </button>
        </div>
      </footer>
    </div>;
}